import { Apple, FacebookOutlined, Google } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Input, Row, Col, Radio, DatePicker, Spin, Typography, Flex,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import FormHeaderLabel from '../../../components/Form/HeaderLabel';
import NumberTextField from '../../../components/Form/NumberTextField';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { CustomerEnum } from '../../../enums/customer';
import { InputFormatEnum } from '../../../enums/inputFormat';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import { ValidationRuleEnum } from '../../../enums/validationRule';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { DateTimeUtils } from '../../../utils/dateTime';
import { PathBuilder } from '../../../utils/pathBuilder';

type CustomerDetailT = {
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber?: string;
  dateOfBirth?: string;
  identificationNumber?: string;
  cardExpiryDate?: string;
  height?: number;
  weight?: number;
  gender?: typeof CustomerEnum.gender;
};

type AccessMethodT = {
  name: string;
  description: string;
};

type RadioDetailT = {
  label: string;
  value: string | number;
};

const genderList: RadioDetailT[] = [
  {
    label: 'Male',
    value: CustomerEnum.gender.male,
  },
  {
    label: 'Female',
    value: CustomerEnum.gender.female,
  },
  {
    label: 'Other',
    value: CustomerEnum.gender.other,
  },
];

const EditCustomer: React.FC = () => {
  const pageTitle = 'Edit account details';
  const navigate = useNavigate();
  const { id: customerId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [customerDetail, setCustomerDetail] = useState<CustomerDetailT>();
  const [accessMethod, setAccessMethod] = useState<AccessMethodT>();

  const { service: getCustomerDetailService } = useServices(ServiceNameEnum.customerMgmt.detail);
  const { service: updateCustomerProfileService } = useServices(ServiceNameEnum.customerMgmt.update);

  const {
    control, formState: { errors }, register, setValue, handleSubmit,
  } = useForm({
    defaultValues: customerDetail,
  });

  const path: PathT[] = [
    {
      label: 'User Account',
      value: '/customer',
    },
    {
      label: `Account : ${customerDetail?.email ?? customerDetail?.firstName ?? ''}`,
      value: `/customer/detail/${customerId}`,
    },
    {
      label: pageTitle,
    },
  ];

  const loadCustomerDetail = async (): Promise<void> => {
    if (customerId) {
      const { data, error } = await getCustomerDetailService.get({
        params: {
          id: customerId,
        },
      });

      if (data) {
        const {
          email = null,
          firstName = null,
          lastName = null,
          mobileNumber,
          dateOfBirth = null,
          accessMethods = [],
          identificationNumber = null,
          cardExpiryDate = null,
          height = null,
          weight = null,
          gender = null,
        } = data;

        const [am] = accessMethods || [];
        setAccessMethod({
          name: am?.name || '',
          description: am?.description || '',
        });

        const formattedDateOfBirth = dateOfBirth ? DateTimeUtils.dateFormat(dateOfBirth) : undefined;
        const formattedCardExpiredAt = cardExpiryDate ? DateTimeUtils.dateFormat(cardExpiryDate) : undefined;

        const formattedData: CustomerDetailT = await {
          email,
          firstName,
          lastName,
          mobileNumber,
          dateOfBirth: formattedDateOfBirth,
          identificationNumber,
          cardExpiryDate: formattedCardExpiredAt,
          height,
          weight,
          gender,
        };

        setCustomerDetail(formattedData);
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message, { toastId: 'allToast' });
      }
    }
  };

  type FormDataT = {
    email: string,
    firstName: string,
    lastName: string,
    mobileNumber?: string,
    dateOfBirth?: string,
    identificationNumber?: string;
    cardExpiryDate?: string;
    height?: number;
    weight?: number;
    gender?: typeof CustomerEnum.gender;
  };

  const onFormSubmit = async (formData: FormDataT): Promise<void> => {
    setIsLoading(true);

    const {
      email,
      firstName,
      lastName,
      mobileNumber,
      dateOfBirth,
      cardExpiryDate,
      identificationNumber,
      height,
      weight,
      gender,
    } = formData;

    const formattedDateOfBirth: string | null = dateOfBirth ? DateTimeUtils.dateFormat(dateOfBirth) : null;
    const formattedCardExpiredAt: string | null = cardExpiryDate ? DateTimeUtils.dateFormat(cardExpiryDate) : null;

    const { data, error } = await updateCustomerProfileService.put({
      email,
      firstName,
      lastName,
      mobileNumber: mobileNumber || null,
      dateOfBirth: formattedDateOfBirth,
      cardExpiryDate: formattedCardExpiredAt,
      identificationNumber,
      height,
      weight,
      gender,
    }, {
      params: {
        id: customerId,
      },
    });

    setIsLoading(false);
    if (data) {
      toast.success('Updated success');
      navigate(PathBuilder.buildPath(RouteNameEnum.customer.detail, { params: { id: customerId } }));
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message);
    }
  };

  const setFormData = async (): Promise<void> => {
    register('email', {});
    setValue('email', customerDetail?.email ?? '');
    setValue('firstName', customerDetail?.firstName ?? '');
    setValue('lastName', customerDetail?.lastName ?? '');
    setValue('mobileNumber', customerDetail?.mobileNumber ?? '');
    setValue('dateOfBirth', customerDetail?.dateOfBirth ?? '');
    setValue('identificationNumber', customerDetail?.identificationNumber ?? '');
    setValue('cardExpiryDate', customerDetail?.cardExpiryDate ?? '');
    setValue('height', customerDetail?.height ?? undefined);
    setValue('weight', customerDetail?.weight ?? undefined);
    if (customerDetail?.gender) {
      setValue('gender', customerDetail?.gender);
    }
  };

  const initData = async (): Promise<void> => {
    await loadCustomerDetail();
  };

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    if (customerDetail) {
      setFormData();
      setIsLoading(false);
    }
  }, [customerDetail]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh',
      }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <AlumlyBreadCrumb path={path} />
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Title level={4} style={{ fontWeight: 700, margin: 0 }}>{pageTitle}</Typography.Title>
          <div style={{ marginTop: 40, paddingBottom: '32px' }}>
            <FormHeaderLabel label="Personal info" />
            <Row gutter={[32, 32]} style={{ marginTop: 8 }}>
              <Col
                sm={12}
                xs={24}
                style={{
                  display: accessMethod?.name === 'password' ? 'none' : 'block',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  {
              accessMethod?.name !== 'password'
              && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {
                    accessMethod?.name === 'google.com'
                    && (
                      <Google fontSize="large" />
                    )
                  }
                  {
                    accessMethod?.name === 'apple.com'
                    && (
                      <Apple fontSize="large" />
                    )
                  }
                  {
                    accessMethod?.name === 'facebook.com'
                    && (
                      <FacebookOutlined fontSize="large" />
                    )
                  }
                  <div style={{ width: '14px' }} />
                  <span>{`Log in with ${accessMethod?.description}`}</span>
                  <div style={{ width: '8px' }} />
                  <span style={{ fontWeight: 700 }}>{customerDetail?.email}</span>
                </div>
              )
            }
                </div>
              </Col>
              <Col
                sm={12}
                xs={24}
                style={{
                  display: accessMethod?.name === 'password' ? 'block' : 'none',
                }}
              >
                <Typography.Text type="secondary">Email</Typography.Text>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: ValidationRuleEnum.email.required.message,
                    pattern: ValidationRuleEnum.email.pattern,
                    maxLength: ValidationRuleEnum.global.maxLength,
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      size="large"
                      status={fieldState.error ? 'error' : ''}
                      placeholder="Email"
                      {...field}
                    />
                  )}
                />
                {errors.email && (
                  <ErrorMessage>
                    { errors.email?.message }
                  </ErrorMessage>
                )}
              </Col>
              <Col xs={24} sm={12}>
                <Typography.Text type="secondary">First name</Typography.Text>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{
                    required: ValidationRuleEnum.global.required.message,
                    maxLength: ValidationRuleEnum.global.maxLength,
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      size="large"
                      status={fieldState.error ? 'error' : ''}
                      placeholder="First name"
                      {...field}
                    />
                  )}
                />
                {errors.firstName && (
                <ErrorMessage>
                  { errors.firstName?.message }
                </ErrorMessage>
                )}
              </Col>
              <Col sm={12} xs={24}>
                <Typography.Text type="secondary">Last name</Typography.Text>
                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: ValidationRuleEnum.global.required.message,
                    maxLength: ValidationRuleEnum.global.maxLength,
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      size="large"
                      status={fieldState.error ? 'error' : ''}
                      placeholder="Last name"
                      {...field}
                    />
                  )}
                />
                {errors.lastName && (
                <ErrorMessage>
                  { errors.lastName?.message }
                </ErrorMessage>
                )}
              </Col>
              <Col sm={12} xs={24}>
                <Typography.Text type="secondary">Phone number</Typography.Text>
                <Controller
                  name="mobileNumber"
                  control={control}
                  rules={{
                    maxLength: ValidationRuleEnum.global.maxLength,
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      size="large"
                      status={fieldState.error ? 'error' : ''}
                      placeholder="Phone number"
                      {...field}
                    />
                  )}
                />
                {errors.mobileNumber && (
                <ErrorMessage>
                  { errors.mobileNumber?.message }
                </ErrorMessage>
                )}
              </Col>
              <Col sm={12} xs={24}>
                <Typography.Text type="secondary">Date of birth</Typography.Text>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      style={{ width: '100%' }}
                      size="large"
                      format={InputFormatEnum.date.format}
                      onChange={(date) => onChange(date ? date.format('YYYY-MM-DD') : '')}
                      value={value ? dayjs(value) : null}
                      disabledDate={(current) => current && current > dayjs().endOf('day')}
                    />
                  )}
                />
              </Col>
              <Col sm={12} xs={24}>
                <Typography.Text type="secondary">Gender</Typography.Text>
                <div>
                  <Controller
                    name="gender"
                    control={control}
                    render={({ field }) => (
                      <Radio.Group {...field}>
                        {
                          genderList.map((gender) => (
                            <Radio key={String(gender.value)} value={gender.value}>
                              {gender.label}
                            </Radio>
                          ))
                        }
                      </Radio.Group>
                    )}
                  />
                </div>
              </Col>
              {/* ID card, Expiry date, Height, Weight removed */}
            </Row>
            <Flex
              gap={32}
              justify="center"
              vertical={false}
              wrap="wrap"
              style={{ marginTop: 32 }}
            >
              <AlumlyButton
                label="rxvWhite"
                width={150}
                icon={<CloseIcon />}
                onClick={
                  () => navigate(PathBuilder.buildPath(RouteNameEnum.customer.detail, { params: { id: customerId } }))
                }
              >
                Cancel
              </AlumlyButton>
              <AlumlyButton
                label="rxv"
                width={150}
                type="submit"
                icon={<CheckIcon />}
              >
                Confirm
              </AlumlyButton>
            </Flex>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditCustomer;
